/**
 * The demo link row of the prototype a turn built, for the Publish menu.
 *
 * A prototype has one link. The turn's own records carry its id once known: the snapshot index
 * entry (resolved by the backend from the turn's prototype) or the message (patched when this tab
 * published). A version built in this session after its prototype was published has neither
 * (its index entry is written locally without one), so the menu showed a plain "Publish" and
 * pressing it would have renamed the prototype's live link (regression test of 19 Sep). The
 * other versions of the same prototype know the link, so any of them answers.
 */
export function demoLinkIdForTurn(args: {
  turnId: string;
  /** Assistant turns of the conversation, in any order. */
  turns: ReadonlyArray<{ llmInteractionId: string; demoLinkId?: unknown }>;
  indexDemoLinkId: (turnId: string) => unknown;
  /** The prototype a turn's version belongs to; null when not known (versions not loaded). */
  prototypeOf: (turnId: string) => string | null | undefined;
}): number | null {
  const { turnId, turns, indexDemoLinkId, prototypeOf } = args;
  const idOf = (lid: string): number | null => {
    const own = turns.find((t) => t.llmInteractionId === lid);
    return positiveId(indexDemoLinkId(lid)) ?? positiveId(own?.demoLinkId);
  };

  const own = idOf(turnId);
  if (own !== null) return own;

  const prototypeId = prototypeOf(turnId);
  if (!prototypeId) return null;
  for (const t of turns) {
    if (t.llmInteractionId === turnId || prototypeOf(t.llmInteractionId) !== prototypeId) continue;
    const sibling = idOf(t.llmInteractionId);
    if (sibling !== null) return sibling;
  }
  return null;
}

/**
 * Ids start at 1. `Number('')` and `Number(null)` are both 0, so an empty field would otherwise
 * read as the id zero.
 */
function positiveId(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}
